/**
 * WebGL2 renderer for the metro map.
 *
 * Owns the GL context, the compiled shader program, per-element VBOs that
 * mirror a {@link GeometrySet} (the highlight-pass source), and the merged
 * per-type VBOs the main pass draws (Stage-2 batching — see
 * `mergedBuffers.ts`). Draw order follows the painter's algorithm already
 * baked into `GeometrySet.elements` (sorted by `renderFloor`), with
 * fill-before-stroke within each element — the merge concatenates in
 * exactly that order, so batching is invisible in the output.
 *
 * Mirrors metro-android `feature/metro-map/.../render/MetroMapRenderer.kt`.
 */
import { BYTES_PER_VERTEX, FLOATS_PER_VERTEX, type GeometrySet } from '../geometry/types'
import { buildMergedBuckets, vertexCountOf } from './mergedBuffers'
import { visibleTiles } from './tileGrid'
import { createDimAlphaAnimator, SETTLE_THRESHOLD } from '../interaction/dimAlphaAnimator'
import {
  compileShader,
  DIM_FRAGMENT_SHADER_SOURCE,
  DIM_VERTEX_SHADER_SOURCE,
  FRAGMENT_SHADER_SOURCE,
  linkProgram,
  VERTEX_SHADER_SOURCE,
} from './shaders'

/** Per-element GPU buffers. Either fill or stroke (or both) may be empty. */
interface ElementBuffers {
  readonly id: string
  readonly fillBuffer: WebGLBuffer | null
  readonly fillVertexCount: number
  readonly strokeBuffer: WebGLBuffer | null
  readonly strokeVertexCount: number
}

/** One merged main-pass VBO part (a bucket's global part or one tile). */
interface MergedVbo {
  readonly buffer: WebGLBuffer
  readonly vertexCount: number
}

/** GPU-side mirror of a `MergedBucketData` — global part + sparse tiles. */
interface MergedBucketVbos {
  readonly global: MergedVbo | null
  readonly tiles: ReadonlyArray<MergedVbo | null>
}

/**
 * Camera transform for the Stage-3 tile culling — the same unscaled
 * canvas-space pan + zoom the host builds the view matrix from. When the
 * host omits it, the renderer draws every tile (defensive fail-safe).
 */
export interface CameraTransform {
  readonly zoom: number
  readonly offsetX: number
  readonly offsetY: number
}

/** Color offset inside the interleaved `[x, y, r, g, b, a]` vertex, in bytes. */
const COLOR_OFFSET_BYTES = 2 * 4

/**
 * Host hooks fired when the browser drops (`onContextLost`) or re-issues
 * (`onContextRestored`) the WebGL2 context. The renderer itself rebuilds all
 * GL resources and re-uploads the retained geometry on restore; these let the
 * host pause / resume its own rAF draw loop and re-apply any host-owned state.
 */
export interface WebGL2RendererOptions {
  readonly onContextLost?: () => void
  readonly onContextRestored?: () => void
  /**
   * The map set may be tile-culled (`CityConfig.mapTileCulling`, default
   * true): only a set holding the tile-order invariant may.
   */
  readonly tileCulling?: boolean
}

export class WebGL2Renderer {
  private readonly gl: WebGL2RenderingContext
  private readonly canvas: HTMLCanvasElement
  private readonly onContextLost?: () => void
  private readonly onContextRestored?: () => void
  private readonly tileCulling: boolean
  // Context-loss guard. On iPadOS Safari the compositor can drop the WebGL2
  // context under GPU pressure (e.g. a full-canvas `backdrop-filter` panel
  // over the map). Without a recovery path every `draw()` would render into a
  // dead context — permanent blank-white until reload. We `preventDefault()`
  // the `webglcontextlost` event (required so the browser fires `restored`),
  // mark this flag so `draw()` no-ops, and rebuild everything on restore.
  private contextLost = false
  // Last geometry uploaded — retained so the restore handler can re-upload it
  // to the fresh context WITHOUT the host re-fetching / re-parsing the MMTG.
  private lastSet: GeometrySet | null = null
  private program!: WebGLProgram
  private aPositionLoc!: number
  private aColorLoc!: number
  private uViewMatrixLoc!: WebGLUniformLocation
  private buffers: ElementBuffers[] = []
  // Stage-2 batching + Stage-3 tiling: the main pass draws these MERGED
  // per-type VBOs — per bucket, the global part first, then the VISIBLE
  // tiles ascending (stations + additionals are tiled; connections +
  // transitions live whole in their global part — see mergedBuffers.ts).
  // The per-element `buffers` above stay as the SOURCE — the highlight
  // pass draws its few elements from them, and merged rebuilds
  // concatenate from the retained `lastSet`. Rebuilt only on geometry
  // upload (theme) or night-set change, never per frame.
  private mergedBuckets: MergedBucketVbos[] = []

  private dimProgram: WebGLProgram | null = null
  private dimVao: WebGLVertexArrayObject | null = null
  private dimQuadVbo: WebGLBuffer | null = null
  private dimColor: [number, number, number, number] = [1, 1, 1, 1]
  private readonly dimAnimator = createDimAlphaAnimator()
  private dimUniformColor: WebGLUniformLocation | null = null
  private dimUniformAlpha: WebGLUniformLocation | null = null

  // Route overlay (UI design spec §5 layer 4) — re-renders the existing
  // pre-tessellated MMTG geometry of selected elements ON TOP of the dim
  // pass at full opacity. No new VBOs: the same `this.buffers` already
  // contain the connection / station / additional geometries; we simply
  // re-issue the draw calls for the subset whose id is in this set, after
  // the dim pass has darkened the base map. This way the route line
  // matches the underlying connection's curve, dashes, and color exactly.
  private highlightedElementIds: ReadonlySet<string> = new Set()

  // Mode visibility filter (TASK-412 M). `null` = no filter, draw all. A
  // non-null set: draw ONLY elements whose id is in the set — the Night Tube
  // network in night mode (`nightElementIds`), or by day the elements a city
  // with a late-night frame draws in normal mode (`dayElementIds`, TASK-1136).
  // Since Stage-2 batching, a filter change rebuilds the MERGED main-pass
  // VBOs (excluded elements are simply not concatenated) — a one-off
  // CPU concat + upload on the rare night toggle, never per frame. The
  // per-element buffers are untouched; no re-tessellation, no re-fetch.
  private nightVisibleIds: ReadonlySet<string> | null = null

  // Per-frame draw-call / vertex counters, reset at the top of every
  // `draw()`. Feeds the DEV-only `map-frame` debug line in the canvas
  // host's rAF loop; the increments are integer adds, cheap enough to
  // run unconditionally in release.
  private readonly frameStats = { draws: 0, verts: 0 }

  constructor(canvas: HTMLCanvasElement, options?: WebGL2RendererOptions) {
    const gl = canvas.getContext('webgl2', {
      antialias: true,
      premultipliedAlpha: false,
    })
    if (gl === null) {
      throw new Error('WebGL2 is not available in this browser')
    }
    this.gl = gl
    this.canvas = canvas
    this.onContextLost = options?.onContextLost
    this.onContextRestored = options?.onContextRestored
    this.tileCulling = options?.tileCulling ?? true

    this.initGlResources()

    // Context loss / restore (iPadOS Safari GPU-pressure recovery). Bound
    // arrow-fn fields so they can be removed in `dispose()`.
    canvas.addEventListener('webglcontextlost', this.handleContextLost)
    canvas.addEventListener('webglcontextrestored', this.handleContextRestored)
  }

  /**
   * Create (or re-create, after a context loss) every GL resource that lives
   * in the GPU context: the two shader programs, attribute / uniform
   * locations, and the dim-pass VAO + full-screen quad VBO. Element geometry
   * buffers are NOT built here — they are (re)built by {@link uploadGeometry},
   * which the restore handler calls with the retained {@link lastSet}.
   *
   * Called once from the constructor and again on every `webglcontextrestored`.
   */
  private initGlResources(): void {
    const gl = this.gl

    const vs = compileShader(gl, gl.VERTEX_SHADER, VERTEX_SHADER_SOURCE)
    const fs = compileShader(gl, gl.FRAGMENT_SHADER, FRAGMENT_SHADER_SOURCE)
    this.program = linkProgram(gl, vs, fs)

    // Locations baked into the shader via `layout(location = …)`, so these
    // are deterministic — but query them anyway to stay resilient if the
    // shader source ever drops the layout qualifiers.
    this.aPositionLoc = gl.getAttribLocation(this.program, 'aPosition')
    this.aColorLoc = gl.getAttribLocation(this.program, 'aColor')

    const uLoc = gl.getUniformLocation(this.program, 'uViewMatrix')
    if (uLoc === null) {
      throw new Error('uViewMatrix uniform not found in linked program')
    }
    this.uViewMatrixLoc = uLoc

    // Dim pass program
    const dimVs = compileShader(gl, gl.VERTEX_SHADER, DIM_VERTEX_SHADER_SOURCE)
    const dimFs = compileShader(gl, gl.FRAGMENT_SHADER, DIM_FRAGMENT_SHADER_SOURCE)
    this.dimProgram = linkProgram(gl, dimVs, dimFs)
    this.dimUniformColor = gl.getUniformLocation(this.dimProgram, 'uDimColor')
    this.dimUniformAlpha = gl.getUniformLocation(this.dimProgram, 'uDimAlpha')

    // Full-screen NDC quad (triangle strip: bl, br, tl, tr)
    const dimQuad = new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1])
    this.dimQuadVbo = gl.createBuffer()
    gl.bindBuffer(gl.ARRAY_BUFFER, this.dimQuadVbo)
    gl.bufferData(gl.ARRAY_BUFFER, dimQuad, gl.STATIC_DRAW)

    this.dimVao = gl.createVertexArray()
    gl.bindVertexArray(this.dimVao)
    gl.bindBuffer(gl.ARRAY_BUFFER, this.dimQuadVbo)
    gl.enableVertexAttribArray(0)
    gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0)
    gl.bindVertexArray(null)

    gl.bindBuffer(gl.ARRAY_BUFFER, null)
  }

  /**
   * `webglcontextlost` handler. Calling `preventDefault()` is REQUIRED — it is
   * the contract by which the browser promises to later fire
   * `webglcontextrestored`; without it the context is gone for good. We drop
   * the (now-invalid) buffer handles without deleting them — the GL calls are
   * no-ops on a lost context and the objects die with the old context — mark
   * the renderer lost so `draw()` short-circuits, then let the host pause its
   * rAF loop.
   */
  private readonly handleContextLost = (event: Event): void => {
    event.preventDefault()
    this.contextLost = true
    this.buffers = []
    this.mergedBuckets = []
    this.onContextLost?.()
  }

  /**
   * `webglcontextrestored` handler. The browser has handed us a fresh backing
   * context on the SAME `WebGL2RenderingContext` object; every prior GL
   * resource is invalid. Rebuild the programs / dim resources, re-upload the
   * retained geometry, clear the lost flag, then let the host resume its rAF
   * loop. The map comes back without a page reload.
   */
  private readonly handleContextRestored = (): void => {
    this.initGlResources()
    if (this.lastSet !== null) {
      this.uploadGeometry(this.lastSet)
    }
    this.contextLost = false
    this.onContextRestored?.()
  }

  /** True while the GL context is lost and awaiting restore. @internal */
  get isContextLost(): boolean {
    return this.contextLost
  }

  /**
   * Upload a `GeometrySet` to the GPU. Previously uploaded buffers are
   * released. The incoming element order (sorted by `renderFloor`) is
   * preserved so that the draw loop can iterate linearly.
   */
  uploadGeometry(set: GeometrySet): void {
    const gl = this.gl

    // Identity guard: the host's geometry effect re-runs on inputs that do
    // NOT change the set (night filter, ready flags) — re-uploading the same
    // `GeometrySet` would delete and recreate ~3 000 VBOs for nothing. Only
    // skip while buffers exist: a context loss empties `buffers`, so the
    // restore path re-uploads the retained set although the reference matches.
    if (set === this.lastSet && this.buffers.length > 0) return

    // Retain the CPU-side geometry so a context-restore can re-upload it to
    // the fresh context without the host re-fetching / re-parsing the MMTG.
    this.lastSet = set

    // Release any previous VBOs first.
    this.releaseBuffers()

    const next: ElementBuffers[] = []
    for (const element of set.elements) {
      let fillBuffer: WebGLBuffer | null = null
      let fillVertexCount = 0
      if (element.fillVertices.length > 0) {
        fillBuffer = gl.createBuffer()
        gl.bindBuffer(gl.ARRAY_BUFFER, fillBuffer)
        gl.bufferData(gl.ARRAY_BUFFER, element.fillVertices, gl.STATIC_DRAW)
        fillVertexCount = element.fillVertices.length / FLOATS_PER_VERTEX
      }

      let strokeBuffer: WebGLBuffer | null = null
      let strokeVertexCount = 0
      if (element.strokeVertices.length > 0) {
        strokeBuffer = gl.createBuffer()
        gl.bindBuffer(gl.ARRAY_BUFFER, strokeBuffer)
        gl.bufferData(gl.ARRAY_BUFFER, element.strokeVertices, gl.STATIC_DRAW)
        strokeVertexCount = element.strokeVertices.length / FLOATS_PER_VERTEX
      }

      next.push({
        id: element.id,
        fillBuffer,
        fillVertexCount,
        strokeBuffer,
        strokeVertexCount,
      })
    }

    gl.bindBuffer(gl.ARRAY_BUFFER, null)
    this.buffers = next
    this.rebuildMergedVbos()
  }

  /**
   * (Re)build the merged per-type main-pass VBOs from the retained
   * `lastSet`, honouring the current night allow-set. Called from
   * {@link uploadGeometry} (theme swap / restore) and from
   * {@link setNightVisibleIds} (filter change) — the only two events that
   * change the main-pass visible geometry.
   */
  private rebuildMergedVbos(): void {
    const gl = this.gl
    this.releaseMergedVbos()
    if (this.lastSet === null) return
    const uploadPart = (vertices: Float32Array): MergedVbo | null => {
      if (vertices.length === 0) return null
      const buffer = gl.createBuffer()
      if (buffer === null) return null
      gl.bindBuffer(gl.ARRAY_BUFFER, buffer)
      gl.bufferData(gl.ARRAY_BUFFER, vertices, gl.STATIC_DRAW)
      return { buffer, vertexCount: vertexCountOf(vertices) }
    }
    for (const bucket of buildMergedBuckets(
      this.lastSet.elements,
      this.nightVisibleIds,
      this.tileCulling,
    )) {
      this.mergedBuckets.push({
        global: bucket.global !== null ? uploadPart(bucket.global) : null,
        tiles: bucket.tiles.map((tile) => (tile !== null ? uploadPart(tile) : null)),
      })
    }
    gl.bindBuffer(gl.ARRAY_BUFFER, null)
  }

  private releaseMergedVbos(): void {
    const gl = this.gl
    for (const bucket of this.mergedBuckets) {
      if (bucket.global !== null) gl.deleteBuffer(bucket.global.buffer)
      for (const tile of bucket.tiles) {
        if (tile !== null) gl.deleteBuffer(tile.buffer)
      }
    }
    this.mergedBuckets = []
  }

  /**
   * Clear and draw the uploaded geometry. Fill and stroke for each element
   * are concatenated back-to-back inside the merged parts so the element's
   * own stroke stays visible over its fill; the next element still paints
   * over both.
   *
   * @param transform - camera pan/zoom for Stage-3 tile culling; tiled
   *   buckets then draw only the tiles intersecting the visible canvas
   *   rect. Omitted → every tile draws (fail-safe, also the pre-init
   *   default in tests).
   */
  draw(
    viewMatrix: Float32Array,
    clearColor: readonly [number, number, number, number],
    transform?: CameraTransform,
  ): void {
    const gl = this.gl

    // Context lost — every GL call would be a no-op over a dead framebuffer
    // (the blank-white failure mode). Skip the whole frame; the restore
    // handler rebuilds everything and the host resumes the loop.
    if (this.contextLost) return

    this.frameStats.draws = 0
    this.frameStats.verts = 0

    const canvas = gl.canvas as HTMLCanvasElement

    gl.viewport(0, 0, canvas.width, canvas.height)
    gl.clearColor(clearColor[0], clearColor[1], clearColor[2], clearColor[3])
    gl.clear(gl.COLOR_BUFFER_BIT)

    gl.useProgram(this.program)
    gl.uniformMatrix4fv(this.uViewMatrixLoc, false, viewMatrix)

    gl.enableVertexAttribArray(this.aPositionLoc)
    gl.enableVertexAttribArray(this.aColorLoc)

    // Main pass — Stage-2 batching + Stage-3 culling: per bucket, draw the
    // global part first, then the visible tiles in ascending index order
    // (the cross-platform frame-order contract). Concatenation order inside
    // each part equals the old per-element fill-before-stroke iteration
    // (see mergedBuffers.ts), and one-tile assignment means no element ever
    // draws twice. The night filter is baked in at rebuild time — no
    // per-element visibility checks on the frame path.
    const tilesInView =
      transform === undefined
        ? null // draw-all fail-safe
        : visibleTiles({
            offsetX: transform.offsetX,
            offsetY: transform.offsetY,
            zoom: transform.zoom,
            viewportW: canvas.width,
            viewportH: canvas.height,
          })
    const drawPart = (part: MergedVbo): void => {
      gl.bindBuffer(gl.ARRAY_BUFFER, part.buffer)
      gl.vertexAttribPointer(this.aPositionLoc, 2, gl.FLOAT, false, BYTES_PER_VERTEX, 0)
      gl.vertexAttribPointer(
        this.aColorLoc,
        4,
        gl.FLOAT,
        false,
        BYTES_PER_VERTEX,
        COLOR_OFFSET_BYTES,
      )
      gl.drawArrays(gl.TRIANGLES, 0, part.vertexCount)
      this.frameStats.draws += 1
      this.frameStats.verts += part.vertexCount
    }
    for (const bucket of this.mergedBuckets) {
      if (bucket.global !== null) drawPart(bucket.global)
      if (tilesInView === null) {
        for (const tile of bucket.tiles) {
          if (tile !== null) drawPart(tile)
        }
      } else {
        for (const tileIndex of tilesInView) {
          const tile = bucket.tiles[tileIndex]
          if (tile !== null && tile !== undefined) drawPart(tile)
        }
      }
    }

    gl.disableVertexAttribArray(this.aPositionLoc)
    gl.disableVertexAttribArray(this.aColorLoc)
    gl.bindBuffer(gl.ARRAY_BUFFER, null)

    // Dim overlay pass — full-viewport quad. Runs after map geometry so
    // it dims the already-rendered map. Uses `rAF`-driven `tick()` — the
    // `draw()` method is invoked once per animation frame by the canvas
    // host, which makes this the natural tick site (no separate keep-alive
    // loop needed, unlike Android which had to work around GL-thread races).
    const nowSec = performance.now() / 1000
    const alpha = this.dimAnimator.tick(nowSec)
    if (alpha > SETTLE_THRESHOLD && this.dimProgram !== null && this.dimVao !== null) {
      gl.useProgram(this.dimProgram)
      gl.bindVertexArray(this.dimVao)
      gl.uniform4f(
        this.dimUniformColor,
        this.dimColor[0],
        this.dimColor[1],
        this.dimColor[2],
        this.dimColor[3],
      )
      gl.uniform1f(this.dimUniformAlpha, alpha)
      gl.enable(gl.BLEND)
      gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA)
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4)
      this.frameStats.draws += 1
      this.frameStats.verts += 4
      gl.bindVertexArray(null)
      gl.disable(gl.BLEND)
    }

    // Route overlay pass — UI design spec §5 layer 4. Re-issues the main
    // shader's draw calls for every uploaded element whose id is in the
    // highlight set, on top of the dim pass. Pre-tessellated MMTG geometry
    // is reused as-is: line connections keep their curves and dashes,
    // station glyphs keep their actual shapes — visually the highlighted
    // route is just the original map drawn through the dim layer.
    if (this.highlightedElementIds.size > 0) {
      gl.useProgram(this.program)
      gl.uniformMatrix4fv(this.uViewMatrixLoc, false, viewMatrix)
      gl.enableVertexAttribArray(this.aPositionLoc)
      gl.enableVertexAttribArray(this.aColorLoc)
      for (const buf of this.buffers) {
        if (!this.highlightedElementIds.has(buf.id)) continue
        if (buf.fillBuffer !== null && buf.fillVertexCount > 0) {
          gl.bindBuffer(gl.ARRAY_BUFFER, buf.fillBuffer)
          gl.vertexAttribPointer(this.aPositionLoc, 2, gl.FLOAT, false, BYTES_PER_VERTEX, 0)
          gl.vertexAttribPointer(
            this.aColorLoc,
            4,
            gl.FLOAT,
            false,
            BYTES_PER_VERTEX,
            COLOR_OFFSET_BYTES,
          )
          gl.drawArrays(gl.TRIANGLES, 0, buf.fillVertexCount)
          this.frameStats.draws += 1
          this.frameStats.verts += buf.fillVertexCount
        }
        if (buf.strokeBuffer !== null && buf.strokeVertexCount > 0) {
          gl.bindBuffer(gl.ARRAY_BUFFER, buf.strokeBuffer)
          gl.vertexAttribPointer(this.aPositionLoc, 2, gl.FLOAT, false, BYTES_PER_VERTEX, 0)
          gl.vertexAttribPointer(
            this.aColorLoc,
            4,
            gl.FLOAT,
            false,
            BYTES_PER_VERTEX,
            COLOR_OFFSET_BYTES,
          )
          gl.drawArrays(gl.TRIANGLES, 0, buf.strokeVertexCount)
          this.frameStats.draws += 1
          this.frameStats.verts += buf.strokeVertexCount
        }
      }
      gl.disableVertexAttribArray(this.aPositionLoc)
      gl.disableVertexAttribArray(this.aColorLoc)
      gl.bindBuffer(gl.ARRAY_BUFFER, null)
    }
  }

  /**
   * Draw-call / vertex counts of the most recent `draw()` frame. Read by
   * the canvas host's DEV-only per-second `map-frame` debug line
   * (verification instrumentation for the map-FPS perf work). The object
   * is reused across frames — read, don't retain.
   */
  get lastFrameStats(): { readonly draws: number; readonly verts: number } {
    return this.frameStats
  }

  /**
   * Seed the dim animator toward [target] (0 = no dim, 1 = full).
   * The actual on-screen alpha ramps via the critically-damped spring
   * in [DimAlphaAnimator]. Host must call [requestRender] (or keep
   * driving `rAF`) so `draw()` runs while [isDimAnimating] is true.
   */
  setDimTarget(target: number, nowSec: number = performance.now() / 1000): void {
    this.dimAnimator.setTarget(target, nowSec)
  }

  /** RGBA of the dim overlay in linear space. Alpha is modulated by the animator. */
  setDimColor(r: number, g: number, b: number, a: number = 1): void {
    this.dimColor = [r, g, b, a]
  }

  /** True while the animator is in flight — host should keep `rAF` ticking. */
  get isDimAnimating(): boolean {
    return !this.dimAnimator.isSettled
  }

  /**
   * Mark the listed element ids (connection / transition / station) as
   * highlighted. They are re-drawn on top of the dim pass at their native
   * pre-tessellated colour, so the route appears as the original map
   * "lifted" out of the desaturated base. Replaces any previous set —
   * pass an empty set (or call {@link clearHighlightedElementIds}) to
   * remove the highlight.
   */
  setHighlightedElementIds(ids: ReadonlySet<string>): void {
    this.highlightedElementIds = ids
  }

  /** Release the highlight set — equivalent to setHighlightedElementIds(emptySet). */
  clearHighlightedElementIds(): void {
    this.highlightedElementIds = new Set()
  }

  /**
   * Apply the mode visibility filter (M). Pass the night allow-set
   * (`nightElementIds(schema)`) to draw ONLY the Night Tube network, or the
   * day allow-set (`dayElementIds`, TASK-1136) to hide night-only elements by
   * day; pass `null` to clear the filter and draw everything. Since
   * Stage-2 batching, a CHANGED set rebuilds the merged main-pass VBOs
   * (excluded elements are not concatenated) — a one-off cost on the rare
   * night toggle. The per-element buffers and the CPU-side geometry are
   * untouched; an identical (same-reference) set is a no-op so the host's
   * effect re-runs stay free.
   */
  setNightVisibleIds(ids: ReadonlySet<string> | null): void {
    if (ids === this.nightVisibleIds) return
    this.nightVisibleIds = ids
    if (!this.contextLost && this.lastSet !== null) {
      this.rebuildMergedVbos()
    }
  }

  /**
   * Internal — the active night-filter size, or `-1` when the filter is off
   * (`null`). Exposed for unit tests to assert bookkeeping without a real
   * GL context.
   * @internal
   */
  _nightVisibleCount(): number {
    return this.nightVisibleIds === null ? -1 : this.nightVisibleIds.size
  }

  /**
   * Internal — number of highlighted element ids currently active.
   * Exposed for unit tests so they can assert bookkeeping without a real
   * GL context.
   * @internal
   */
  _highlightedElementCount(): number {
    return this.highlightedElementIds.size
  }

  /** Release all GPU resources. Call from the React effect cleanup. */
  dispose(): void {
    this.canvas.removeEventListener('webglcontextlost', this.handleContextLost)
    this.canvas.removeEventListener('webglcontextrestored', this.handleContextRestored)
    this.releaseBuffers()
    this.gl.deleteProgram(this.program)
    if (this.dimProgram !== null) this.gl.deleteProgram(this.dimProgram)
    if (this.dimVao !== null) this.gl.deleteVertexArray(this.dimVao)
    if (this.dimQuadVbo !== null) this.gl.deleteBuffer(this.dimQuadVbo)
  }

  private releaseBuffers(): void {
    const gl = this.gl
    for (const buf of this.buffers) {
      if (buf.fillBuffer !== null) gl.deleteBuffer(buf.fillBuffer)
      if (buf.strokeBuffer !== null) gl.deleteBuffer(buf.strokeBuffer)
    }
    this.buffers = []
    this.releaseMergedVbos()
  }
}
